import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { productService } from '../services/productService';
import { checkoutService } from '../services/checkoutService';
import Swal from 'sweetalert2';

const ProductDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { addToCart } = useCart();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cep, setCep] = useState('');
    const [shippingRates, setShippingRates] = useState([]);
    const [loadingShipping, setLoadingShipping] = useState(false);
    const [error, setError] = useState('');
    const lastProcessedId = useRef(null);

    // Carregar produto
    useEffect(() => {
        // Evita chamadas duplicadas causadas pelo React.StrictMode
        if (lastProcessedId.current === id) {
            return;
        }
        
        lastProcessedId.current = id;
        
        const loadProduct = async () => {
            try {
                setLoading(true);
                const apiProduct = await productService.getProductById(id);
                const formattedProduct = productService.formatProductForFrontend(apiProduct);
                setProduct(formattedProduct);
            } catch (err) {
                console.error('Erro ao carregar produto:', err);
                setError('Produto não encontrado');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadProduct();
        }
    }, [id]);

    // Configurações de envio
    const shippingConfig = {
        originCep: '81670-130',
        productWeight: 0.2, // 200g por unidade de produto
        productDimensions: {
            length: 20,
            width: 15,
            height: 10
        }
    };


    if (loading) {
        return (
            <section className="wrapper style1">
                <div className="container">
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        fontSize: '1.2rem',
                        color: '#666'
                    }}>
                        <i className="fas fa-spinner fa-spin" style={{ marginRight: '10px' }}></i>
                        Carregando produto...
                    </div>
                </div>
            </section>
        );
    }

    if (error || !product) {
        return (
            <section className="wrapper style1">
                <div className="container">
                    <div style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        backgroundColor: '#ffebee',
                        border: '1px solid #ffcdd2',
                        borderRadius: '8px',
                        color: '#c62828'
                    }}>
                        <i className="fas fa-exclamation-triangle" style={{ marginRight: '10px' }}></i>
                        {error || 'Produto não encontrado'}
                    </div>
                </div>
            </section>
        );
    }


    const handleAddToCart = () => {
        addToCart(product);
        Swal.fire({
            title: 'Produto Adicionado!',
            text: `"${product.title}" foi adicionado ao carrinho`,
            icon: 'success',
            showCancelButton: true,
            confirmButtonText: 'Continuar Comprando',
            cancelButtonText: 'OK',
            confirmButtonColor: '#edf507',
            cancelButtonColor: '#6c757d',
            customClass: {
                confirmButton: 'swal-confirm-button',
                cancelButton: 'swal-cancel-button'
            }
        }).then((result) => {
            if (result.isConfirmed) {
                navigate('/');
            }
        });
    };

    const calculateShipping = async () => {
        const cleanCep = cep.replace(/\D/g, ''); // Remove hífen da máscara
        if (!cleanCep || cleanCep.length !== 8) {
            setError('Digite um CEP válido (8 dígitos)');
            return;
        }

        setLoadingShipping(true);
        setError('');
        setShippingRates([]);

        try {
            // Calcular peso em gramas (200g por unidade de produto)
            const pesoGrams = shippingConfig.productWeight * 1000; // 0.2kg = 200g

            // Buscar dados de frete da API
            const shippingData = await checkoutService.calculateShipping(cleanCep, pesoGrams);

            // Mapear a resposta do backend para o formato esperado pelo frontend
            const mappedShippingData = shippingData.map(item => ({
                service: item.servico,
                name: item.servico,
                price: item.preco_frete,
                deliveryTime: `${item.prazo_minimo}-${item.prazo_maximo} dias úteis após a postagem`
            }));

            setShippingRates(mappedShippingData);
        } catch (err) {
            console.error('Erro ao calcular frete:', err);
            setError('Erro ao calcular frete. Verifique o CEP e tente novamente.');
        } finally {
            setLoadingShipping(false);
        }
    };

    const formatCep = (value) => {
        // Remove tudo que não é dígito
        const numbers = value.replace(/\D/g, '');
        // Limita a 8 dígitos
        const limitedNumbers = numbers.slice(0, 8);
        // Aplica máscara 99999-999
        if (limitedNumbers.length <= 5) {
            return limitedNumbers;
        } else {
            return `${limitedNumbers.slice(0, 5)}-${limitedNumbers.slice(5)}`;
        }
    };

    return (
        <section className="wrapper style1">
            <div className="container">
                <div className="row gtr-200">
                    <div className="col-6 col-12-narrower">
                        <div style={{ textAlign: 'center' }}>
                            <img
                                src={product.image}
                                alt={product.alt}
                                style={{
                                    width: '100%',
                                    maxWidth: '400px',
                                    height: '400px',
                                    objectFit: 'cover',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                }}
                            />
                        </div>
                    </div>
                    <div className="col-6 col-12-narrower">
                        <div style={{ padding: '20px 0' }}>
                            <h1 style={{
                                fontSize: '2.5rem',
                                fontWeight: 'bold',
                                color: '#000000',
                                margin: '0 0 20px 0',
                                textTransform: 'uppercase',
                                letterSpacing: '1px'
                            }}>
                                {product.title}
                            </h1>

                            <div style={{
                                fontSize: '2rem',
                                fontWeight: 'bold',
                                color: '#000000',
                                margin: '0 0 20px 0',
                                padding: '15px',
                                backgroundColor: '#f8f8f8',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                R$ {product.currentPrice.toFixed(2).replace('.', ',')}
                            </div>

                            <div 
                                style={{
                                    fontSize: '1.1rem',
                                    color: '#666',
                                    lineHeight: '1.6',
                                    margin: '0 0 30px 0'
                                }}
                                dangerouslySetInnerHTML={{ __html: product.description }}
                            />

                            <h3 style={{
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                color: '#000000',
                                margin: '0 0 15px 0'
                            }}>
                                Características:
                            </h3>

                            <ul style={{
                                listStyle: 'none',
                                padding: '0',
                                margin: '0 0 30px 0'
                            }}>
                                {product.features && product.features.map((feature, index) => (
                                    <li key={index} style={{
                                        padding: '8px 0',
                                        borderBottom: '1px solid #f0f0f0',
                                        color: '#333',
                                        fontSize: '1rem'
                                    }}>
                                        ✓ {feature}
                                    </li>
                                ))}
                            </ul>

                            {/* Campo de CEP para Frete */}
                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{
                                    fontSize: '1.1rem',
                                    fontWeight: 'bold',
                                    color: '#000000',
                                    margin: '0 0 5px 0'
                                }}>
                                    Calcular Frete:
                                </h4>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    <input
                                        type="text"
                                        placeholder="Digite seu CEP"
                                        value={cep}
                                        onChange={(e) => setCep(formatCep(e.target.value))}
                                        style={{
                                            flex: '1',
                                            padding: '12px',
                                            border: '2px solid #e0e0e0',
                                            borderRadius: '6px',
                                            fontSize: '1rem',
                                            outline: 'none'
                                        }}
                                        onFocus={(e) => {
                                            e.target.style.borderColor = '#000000';
                                        }}
                                        onBlur={(e) => {
                                            e.target.style.borderColor = '#e0e0e0';
                                        }}
                                    />
                                    <button
                                        onClick={calculateShipping}
                                        disabled={loadingShipping}
                                        style={{
                                            padding: '12px 20px',
                                            backgroundColor: loadingShipping ? '#ccc' : '#f8f8f8',
                                            color: '#000000',
                                            border: '2px solid #e0e0e0',
                                            borderRadius: '6px',
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            cursor: loadingShipping ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!loadingShipping) {
                                                e.target.style.backgroundColor = '#e0e0e0';
                                                e.target.style.borderColor = '#000000';
                                            }
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!loadingShipping) {
                                                e.target.style.backgroundColor = '#f8f8f8';
                                                e.target.style.borderColor = '#e0e0e0';
                                            }
                                        }}
                                    >
                                        {loadingShipping ? 'Calculando...' : 'Calcular'}
                                    </button>
                                </div>

                                {/* Mensagem de erro */}
                                {error && (
                                    <div style={{
                                        color: '#d32f2f',
                                        fontSize: '0.9rem',
                                        marginBottom: '10px',
                                        padding: '8px',
                                        backgroundColor: '#ffebee',
                                        borderRadius: '4px',
                                        border: '1px solid #ffcdd2'
                                    }}>
                                        {error}
                                    </div>
                                )}

                                {/* Opções de frete */}
                                {shippingRates && shippingRates.length > 0 && (
                                    <div style={{
                                        backgroundColor: '#f8f8f8',
                                        borderRadius: '6px',
                                        padding: '15px',
                                        border: '2px solid #e0e0e0'
                                    }}>
                                        <h5 style={{
                                            fontSize: '1rem',
                                            fontWeight: 'bold',
                                            color: '#000000',
                                            margin: '0 0 10px 0'
                                        }}>
                                            Opções de Entrega:
                                        </h5>
                                        {shippingRates.map((rate, index) => (
                                            <div key={index} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                padding: '8px 0',
                                                borderBottom: index < shippingRates.length - 1 ? '1px solid #e0e0e0' : 'none'
                                            }}>
                                                <div>
                                                    <div style={{
                                                        fontWeight: 'bold',
                                                        color: '#000000',
                                                        fontSize: '0.95rem'
                                                    }}>
                                                        {rate.name}
                                                    </div>
                                                    <div style={{
                                                        color: '#666',
                                                        fontSize: '0.85rem'
                                                    }}>
                                                        {rate.deliveryTime}
                                                    </div>
                                                </div>
                                                <div style={{
                                                    fontWeight: 'bold',
                                                    color: '#000000',
                                                    fontSize: '1rem'
                                                }}>
                                                    R$ {rate.price.toFixed(2).replace('.', ',')}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Botão Adicionar ao Carrinho */}
                            <div style={{ textAlign: 'center' }}>
                                <button
                                    onClick={handleAddToCart}
                                    style={{
                                        width: '100%',
                                        padding: '20px 30px',
                                        backgroundColor: '#000000',
                                        color: '#ffffff',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '1.2rem',
                                        fontWeight: 'bold',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = '#333333';
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.3)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = '#000000';
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.2)';
                                    }}
                                >
                                    Adicionar ao Carrinho
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProductDetail;
